import React, { useState, useEffect } from "react";
import {
  Card,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Drawer,
  Alert,
  Snackbar,
  Box,
  Chip,
  Autocomplete,
} from "@mui/material";
import VuiBox from "components/VuiBox";
import VuiButton from "components/VuiButton";
import VuiTypography from "components/VuiTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Table from "examples/Tables/Table";
import VuiPagination from "components/VuiPagination";
import Icon from "@mui/material/Icon";
import { Map, People, Assignment } from "@mui/icons-material";
import axios from "axios";
function RegionManagement() {
  const [regions, setRegions] = useState([]);
  const [users, setUsers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(12);
  const [addRegionOpen, setAddRegionOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [activeTab, setActiveTab] = useState(0);
  const [sites, setSites] = useState([]);
  const [selectedSites, setSelectedSites] = useState([]);
  const [newRegionData, setNewRegionData] = useState({
    name: "",
    description: "",
  });

  const token = localStorage.getItem("token");

  // Calculate pagination variables with safe defaults
  const totalPages = Math.ceil(regions.length / rowsPerPage) || 1;
  const displayedRows = Array.isArray(regions)
    ? regions.slice((page - 1) * rowsPerPage, page * rowsPerPage)
    : [];

  // Table columns definition
  const columns = [
    { name: "Region ID", align: "left" },
    { name: "Name", align: "left" },
    { name: "Description", align: "left" },
    { name: "Assigned Users", align: "left" },
    { name: "Sites", align: "left" },
    { name: "Actions", align: "center" },
  ];

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch regions
      const regionsResponse = await fetch(
        "https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/regions",
        { headers: { Authorization: `${token}` } }
      );

      if (!regionsResponse.ok) {
        if (regionsResponse.status === 502) {
          showSnackbar("API Gateway is unavailable", "error");
        }
        throw new Error(`HTTP error! status: ${regionsResponse.status}`);
      }

      const regionsData = await regionsResponse.json();
      let parsedRegions = Array.isArray(regionsData) ? regionsData : [];

      // If it's an object with body property
      if (regionsData && typeof regionsData === "object" && regionsData.body) {
        parsedRegions =
          typeof regionsData.body === "string" ? JSON.parse(regionsData.body) : regionsData.body;
      }

      // Ensure we have an array
      if (!Array.isArray(parsedRegions)) {
        parsedRegions = [];
      }

      setRegions(parsedRegions);

      // Fetch users
      const usersResponse = await fetch(
        "https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/admin/users",
        { headers: { Authorization: `${token}` } }
      );

      if (usersResponse.ok) {
        const usersData = await usersResponse.json();
        const formattedUsers = Array.isArray(usersData)
          ? usersData.map((user) => ({
              id: user[0],
              user_id: user[0], // Add both id and user_id for compatibility
              email: user[1],
              first_name: user[3],
              last_name: user[4],
            }))
          : [];
        setUsers(formattedUsers);
      }

      // Fetch assignments
      const assignmentsResponse = await fetch(
        "https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/region/assignments",
        { headers: { Authorization: `${token}` } }
      );

      if (assignmentsResponse.ok) {
        const assignmentsData = await assignmentsResponse.json();
        setAssignments(Array.isArray(assignmentsData) ? assignmentsData : []);
      }
      const sitesResponse = await fetch(
        "https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/admin/sites",
        { headers: { Authorization: `${token}` } }
      );
      if (sitesResponse.ok) {
        const sitesData = await sitesResponse.json();
        setSites(Array.isArray(sitesData) ? sitesData : []);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      showSnackbar("Error loading data", "error");
      setRegions([]);
      setUsers([]);
      setAssignments([]);
    } finally {
      setLoading(false);
    }
  };

  const getAssignedUsers = (regionId) => {
    return Array.isArray(assignments) ? assignments.filter((a) => a.region_id === regionId) : [];
  };

  const getUserName = (userId) => {
    const user = Array.isArray(users) ? users.find((u) => u.id === userId) : null;
    return user ? `${user.first_name} ${user.last_name}` : "Unknown User";
  };

  const handleEditClick = async (region) => {
    setSelectedRegion({
      id: region.region_id,
      name: region.name,
      description: region.description,
    });

    try {
      const assigned = getAssignedUsers(region.region_id);
      setSelectedUsers(
        assigned.map((a) => {
          const user = users.find((u) => u.id === a.user_id);
          return (
            user || {
              id: a.user_id,
              user_id: a.user_id,
              first_name: a.first_name,
              last_name: a.last_name,
            }
          );
        })
      );

      //  Set assigned sites
      const assignedSites = getAssignedSites(region.region_id);
      setSelectedSites(assignedSites);
    } catch (error) {
      console.error("Error fetching region assignments:", error);
      setSelectedUsers([]);
      setSelectedSites([]); // Clear just in case
    }

    setDrawerOpen(true);
  };

  const handleDeleteClick = (region) => {
    setSelectedRegion({
      id: region.region_id,
      name: region.name,
    });
    setDeleteConfirmOpen(true);
  };

  const handleAddRegion = async () => {
    try {
      const response = await fetch(
        "https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/region/create",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
          body: JSON.stringify(newRegionData),
        }
      );

      if (response.ok) {
        showSnackbar("Region added successfully", "success");
        setAddRegionOpen(false);
        setNewRegionData({
          name: "",
          description: "",
        });
        await fetchData();
      } else {
        showSnackbar("Failed to add region", "error");
      }
    } catch (error) {
      showSnackbar("Error adding region", "error");
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleUpdateRegion = async () => {
    try {
      const response = await fetch(
        `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/region/update`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
          body: JSON.stringify({
            region_id: selectedRegion.id,
            name: selectedRegion.name,
            description: selectedRegion.description,
          }),
        }
      );

      if (response.ok) {
        showSnackbar("Region updated successfully", "success");
        setDrawerOpen(false);
        await fetchData();
      } else {
        const errorData = await response.json();
        showSnackbar(errorData.error || "Failed to update region", "error");
      }
    } catch (error) {
      showSnackbar("Error updating region", "error");
    }
  };

  const handleDeleteRegion = async () => {
    try {
      const response = await fetch(
        `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/region/delete?region_id=${selectedRegion.id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `${token}`,
          },
        }
      );

      if (response.ok) {
        showSnackbar("Region deleted successfully", "success");
        setDeleteConfirmOpen(false);
        await fetchData();
      } else {
        showSnackbar("Failed to delete region", "error");
      }
    } catch (error) {
      showSnackbar("Error deleting region", "error");
    }
  };

  const handleAssignUsers = async () => {
    try {
      // Get current assignments for this region
      const currentAssignments = getAssignedUsers(selectedRegion.id);
      const currentUserIds = currentAssignments.map((a) => a.user_id);
      const newUserIds = selectedUsers.map((u) => u.id || u.user_id);

      // Users to add
      const addUsers = newUserIds.filter((id) => !currentUserIds.includes(id));
      // Users to remove
      const removeUsers = currentUserIds.filter((id) => !newUserIds.includes(id));

      // Process additions
      if (addUsers.length > 0) {
        await Promise.all(
          addUsers.map(async (userId) => {
            const body = {
              userId: userId,
              regionId: selectedRegion.id,
            };

            const response = await fetch(
              `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/region/assign-user`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `${token}`,
                },
                body: JSON.stringify(body),
              }
            );
            if (!response.ok) throw new Error("Failed to assign user");
          })
        );
      }

      // Process removals
      if (removeUsers.length > 0) {
        await Promise.all(
          removeUsers.map(async (userId) => {
            const response = await fetch(
              `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/region/remove-assignment?user_id=${userId}&region_id=${selectedRegion.id}`,
              {
                method: "DELETE",
                headers: { Authorization: `${token}` },
              }
            );
            if (!response.ok) throw new Error("Failed to remove assignment");
          })
        );
      }

      showSnackbar("User assignments updated successfully", "success");
      await fetchData();
    } catch (error) {
      console.error("Error updating user assignments:", error);
      showSnackbar("Error updating user assignments", "error");
    }
  };
  const getAssignedSites = (regionId) => {
    if (!sites || !Array.isArray(sites)) return [];
    return sites.filter((site) => site && String(site.region_id) === String(regionId));
  };
  const handleAssignSites = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        "https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/region/bulk-assign-sites",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
          body: JSON.stringify({
            region_id: selectedRegion.id,
            site_ids: selectedSites.map((site) => site.site_id || site),
          }),
        }
      );

      if (response.ok) {
        showSnackbar("Sites assigned successfully", "success");
        await fetchData(); // Refresh the data
      } else {
        const errorData = await response.json();
        showSnackbar(errorData.error || "Failed to assign sites", "error");
      }
    } catch (error) {
      console.error("Failed to assign sites:", error);
      showSnackbar("Failed to assign sites", "error");
    }
  };
  const rows = displayedRows.map((region) => {
    const regionSites = getAssignedSites(region.region_id);

    return {
      "Region ID": region.region_id,
      Name: region.name,
      Description: region.description,
      "Assigned Users": (
        <VuiBox display="flex" gap={1} flexWrap="wrap">
          {getAssignedUsers(region.region_id).map((assignment) => (
            <Chip
              key={`${assignment.user_id}-${region.region_id}`}
              label={`${assignment.first_name} ${assignment.last_name}`}
              size="small"
              color="primary"
            />
          ))}
        </VuiBox>
      ),
      Sites: (
        <VuiBox display="flex" gap={1} flexWrap="wrap">
          {regionSites.length > 0 ? (
            regionSites.map((site) => (
              <Chip
                key={site.site_id}
                label={site.name || `Site ${site.site_id}`}
                size="small"
                color="secondary"
              />
            ))
          ) : (
            <VuiTypography variant="caption" color="textSecondary">
              No sites assigned
            </VuiTypography>
          )}
        </VuiBox>
      ),
      Actions: (
        <VuiBox display="flex" gap={1} justifyContent="flex-end">
          <VuiButton
            color="info"
            size="small"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleEditClick(region);
            }}
          >
            <Icon>edit</Icon>
          </VuiButton>
          <VuiButton
            color="error"
            size="small"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleDeleteClick(region);
            }}
          >
            <Icon>delete</Icon>
          </VuiButton>
        </VuiBox>
      ),
    };
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        <Card>
          <VuiBox display="flex" justifyContent="space-between" alignItems="center" p={2}>
            <VuiTypography variant="lg" color="white">
              Region Management
            </VuiTypography>
            <VuiButton color="info" onClick={() => setAddRegionOpen(true)}>
              Add Region
            </VuiButton>
          </VuiBox>

          {loading ? (
            <VuiBox display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </VuiBox>
          ) : regions.length > 0 ? (
            <>
              <Table columns={columns} rows={rows} />
              <VuiBox display="flex" justifyContent="center" p={2}>
                <VuiPagination>
                  <VuiPagination item onClick={() => setPage(page - 1)} disabled={page === 1}>
                    <Icon>keyboard_arrow_left</Icon>
                  </VuiPagination>
                  {Array.from({ length: totalPages }, (_, index) => (
                    <VuiPagination
                      key={index + 1}
                      item
                      active={page === index + 1}
                      onClick={() => setPage(index + 1)}
                    >
                      {index + 1}
                    </VuiPagination>
                  ))}
                  <VuiPagination
                    item
                    onClick={() => setPage(page + 1)}
                    disabled={page === totalPages}
                  >
                    <Icon>keyboard_arrow_right</Icon>
                  </VuiPagination>
                </VuiPagination>
              </VuiBox>
            </>
          ) : (
            <VuiBox display="flex" justifyContent="center" py={4}>
              <VuiTypography variant="body2" color="textSecondary">
                No regions found or error loading data
              </VuiTypography>
            </VuiBox>
          )}
        </Card>
      </VuiBox>

      {/* Add Region Dialog */}
      <Dialog open={addRegionOpen} onClose={() => setAddRegionOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Region</DialogTitle>
        <DialogContent>
          <VuiBox component="form" display="flex" flexDirection="column" gap={2} p={2}>
            <TextField
              label="Name"
              value={newRegionData.name}
              onChange={(e) => setNewRegionData({ ...newRegionData, name: e.target.value })}
              fullWidth
              sx={{ 
                  '& .MuiOutlinedInput-root': { width: '100%' }, 
                  '& .MuiInputBase-input': { width: '100% !important' } 
                }}
              InputLabelProps={{
                shrink: true,
                style: { fontSize: "0.875rem" },
              }}
              InputProps={{
                style: { fontSize: "0.875rem", width: '100%' },
              }}
            />
            <TextField
              label="Description"
              value={newRegionData.description}
              onChange={(e) => setNewRegionData({ ...newRegionData, description: e.target.value })}
              fullWidth
              multiline
              rows={4}
              sx={{ 
                  '& .MuiOutlinedInput-root': { width: '100%' }, 
                  '& .MuiInputBase-input': { width: '100% !important' } 
                }}
              InputLabelProps={{
                shrink: true,
                style: { fontSize: "0.875rem" },
              }}
              InputProps={{
                style: { fontSize: "0.875rem", width: '100%' },
              }}
            />
          </VuiBox>
        </DialogContent>
        <DialogActions>
          <VuiButton color="error" onClick={() => setAddRegionOpen(false)}>
            Cancel
          </VuiButton>
          <VuiButton color="info" onClick={handleAddRegion}>
            Add Region
          </VuiButton>
        </DialogActions>
      </Dialog>

      {/* Edit Region Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedRegion(null);
          setSelectedUsers([]);
          setSelectedSites([]);
        }}
        PaperProps={{
          sx: { width: "400px" },
        }}
      >
        {selectedRegion && (
          <VuiBox p={3}>
            <VuiTypography variant="h6" mb={3}>
              Edit Region
            </VuiTypography>
            <VuiBox display="flex" flexDirection="column" gap={2}>
              <TextField
                label="Name"
                value={selectedRegion.name}
                onChange={(e) => setSelectedRegion({ ...selectedRegion, name: e.target.value })}
                fullWidth
                sx={{ 
                  '& .MuiOutlinedInput-root': { width: '100%' }, 
                  '& .MuiInputBase-input': { width: '100% !important' } 
                }}
                InputLabelProps={{ shrink: true, style: { fontSize: "0.875rem" } }}
                InputProps={{ style: { fontSize: "0.875rem", width: '100%' } }}
              />
              <TextField
                label="Description"
                value={selectedRegion.description}
                onChange={(e) =>
                  setSelectedRegion({ ...selectedRegion, description: e.target.value })
                }
                fullWidth
                multiline
                rows={4}
                sx={{ 
                  '& .MuiOutlinedInput-root': { width: '100%' }, 
                  '& .MuiInputBase-input': { width: '100% !important' } 
                }}
                InputLabelProps={{ shrink: true, style: { fontSize: "0.875rem" } }}
                InputProps={{ style: { fontSize: "0.875rem", width: '100%' } }}
              />
              <Autocomplete
                multiple
                value={selectedUsers}
                onChange={(event, newValue) => setSelectedUsers(newValue)}
                options={users}
                getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
                isOptionEqualToValue={(option, value) =>
                  (option.id && value.id && option.id === value.id) ||
                  (option.user_id && value.user_id && option.user_id === value.user_id)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Assigned Users"
                    placeholder="Select users"
                    InputLabelProps={{ shrink: true, style: { fontSize: "0.875rem" } }}
                    InputProps={{
                      ...params.InputProps,
                      style: { fontSize: "0.875rem" },
                    }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={`${option.first_name} ${option.last_name}`}
                      {...getTagProps({ index })}
                      key={option.id || option.user_id}
                    />
                  ))
                }
              />
              <Autocomplete
                multiple
                value={selectedSites}
                onChange={(event, newValue) => setSelectedSites(newValue)}
                options={sites}
                getOptionLabel={(option) => option.name || `Site ${option.site_id || option}`}
                isOptionEqualToValue={(option, value) =>
                  (option.site_id && value.site_id && option.site_id === value.site_id) ||
                  option === value
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Assigned Sites"
                    placeholder="Select sites"
                    InputLabelProps={{ shrink: true, style: { fontSize: "0.875rem" } }}
                    InputProps={{
                      ...params.InputProps,
                      style: { fontSize: "0.875rem" },
                    }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option.name || `Site ${option.site_id || option}`}
                      {...getTagProps({ index })}
                      key={option.site_id || option}
                    />
                  ))
                }
              />
              <VuiBox display="flex" gap={2} mt={2}>
                <VuiButton
                  color="error"
                  fullWidth
                  onClick={() => {
                    setDrawerOpen(false);
                    setSelectedRegion(null);
                    setSelectedUsers([]);
                  }}
                >
                  Cancel
                </VuiButton>
                <VuiButton
                  color="info"
                  fullWidth
                  onClick={async () => {
                    await handleUpdateRegion();
                    await handleAssignUsers();
                    await handleAssignSites();
                    setDrawerOpen(false);
                  }}
                >
                  Save Changes
                </VuiButton>
              </VuiBox>
            </VuiBox>
          </VuiBox>
        )}
      </Drawer>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <VuiTypography>
            Are you sure you want to delete region {selectedRegion?.name}?
          </VuiTypography>
        </DialogContent>
        <DialogActions>
          <VuiButton color="info" onClick={() => setDeleteConfirmOpen(false)}>
            Cancel
          </VuiButton>
          <VuiButton color="error" onClick={handleDeleteRegion}>
            Delete
          </VuiButton>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default RegionManagement;
